import { useMemo, useState } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { useDebounce } from 'react-use';
import { assert } from 'ts-essentials';
import * as z from 'zod';

import { useTrip } from '@src/contexts/trip-context';
import { useAnalytics } from '@src/features/analytics/hooks';
import { useProfile } from '@src/features/profile/contexts/profile-context';
import { guestApi } from '@src/services';

type QueryOptions = Omit<
  UseQueryOptions<
    Awaited<ReturnType<typeof guestApi.getGuestConsents>> | undefined,
    unknown,
    Awaited<ReturnType<typeof guestApi.getGuestConsents>>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    any
  >,
  'queryKey' | 'queryFn' | 'enabled'
>;

const useValidatedEmail = (email?: string) => {
  return useMemo<string | undefined>(() => {
    if (!email) {
      return undefined;
    }

    const emailSchema = z.string().email();
    const result = emailSchema.safeParse(email);

    if (result.success) {
      return result.data;
    }

    return undefined;
  }, [email]);
};

/**
 * Load consents and guest identification data for a given email address.
 * If the guest is not logged in, they get identified on Segment.
 */
export const useGuestConsents = (email: string | undefined, queryOptions?: QueryOptions) => {
  const validEmail = useValidatedEmail(email);
  const [debouncedEmail, setDebouncedEmail] = useState<string | undefined>(undefined);
  useDebounce(
    () => {
      setDebouncedEmail(validEmail);
    },
    500,
    [validEmail],
  );

  const trip = useTrip()?.trip;
  const { trackIdentification } = useAnalytics();
  const profileQuery = useProfile();

  const guestConsentsQuery = useQuery(
    ['guest-consents', debouncedEmail],
    async () => {
      if (!debouncedEmail) {
        return undefined;
      }
      return guestApi.getGuestConsents({ params: { email: debouncedEmail } });
    },
    {
      enabled: !!debouncedEmail,
      keepPreviousData: false,
      retry: false,
      retryOnMount: false,
      refetchOnWindowFocus: false,
      ...queryOptions,
    },
  );

  /**
   * Once a guest has been identified, and they are not logged in or don't have a guestId assigned to their profile,
   * the guest gets identified on Segment.
   */
  const hasProfileWithGuestId = profileQuery?.isSuccess && !!profileQuery?.data?.guestId;
  useQuery(
    ['guest-identification', guestConsentsQuery.isSuccess, hasProfileWithGuestId],
    async () => {
      assert(guestConsentsQuery.isSuccess);
      const { data } = guestConsentsQuery.data;
      const isMainGuest = trip?.guests.find(({ email: guestEmail }) => guestEmail === debouncedEmail)?.isMainGuest;

      if (trip && !isMainGuest) {
        return;
      }

      await trackIdentification({
        firstName: data.firstName,
        lastName: data.lastName,
        guestId: data.guestId,
        email: data.email,
      });
    },
    {
      enabled: guestConsentsQuery.isSuccess && !hasProfileWithGuestId,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );

  return guestConsentsQuery;
};
